import React from 'react';
import { Sparkles } from 'lucide-react';
import { Translations } from '../../i18n/translations';

export interface FeedStats {
  loaded: number;
  flagged: number;
  cleansed: number;
}

interface SettingsStatsProps {
  stats: FeedStats;
  t: Translations;
}

// Glanceable summary at the top of Settings: how many headlines were
// loaded, flagged as clickbait, and actually rewritten in Cleanse mode.
// Hidden entirely when nothing has been loaded yet so a fresh tab doesn't
// open Settings to a row of zeroes.
export const SettingsStats: React.FC<SettingsStatsProps> = ({ stats, t }) => {
  if (stats.loaded === 0) return null;

  const summary = stats.flagged === 0
    ? t.statsSummaryClean
    : stats.cleansed === 0
      ? t.statsSummaryFlagged.replace('{flagged}', String(stats.flagged))
      : t.statsSummaryBoth
          .replace('{flagged}', String(stats.flagged))
          .replace('{cleansed}', String(stats.cleansed));

  return (
    <div className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-3 sm:p-4 mb-4">
      <h3 className="font-bold mb-3 flex items-center gap-2 text-sm sm:text-base text-gray-900 dark:text-white">
        <Sparkles className="w-4 h-4" aria-hidden="true" />
        {t.statsTitle}
      </h3>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat value={stats.loaded} label={t.statsLoaded} tone="neutral" />
        <Stat value={stats.flagged} label={t.statsFlagged} tone="warn" />
        <Stat value={stats.cleansed} label={t.statsCleansed} tone="good" />
      </div>
      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{summary}</p>
    </div>
  );
};

const Stat: React.FC<{ value: number; label: string; tone: 'neutral' | 'warn' | 'good' }> = ({ value, label, tone }) => {
  const valueClass =
    tone === 'warn'
      ? 'text-red-700 dark:text-red-400'
      : tone === 'good'
        ? 'text-green-700 dark:text-green-400'
        : 'text-gray-900 dark:text-white';
  return (
    <div className="border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 text-center">
      <div className={`text-xl sm:text-2xl font-bold font-serif ${valueClass}`}>{value}</div>
      <div className="text-[10px] sm:text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-0.5">
        {label}
      </div>
    </div>
  );
};
